import clsx from "clsx";

import type { PDPProduct } from "@/app/[locale]/(static)/product/[product]/page";
import { useLocale } from "next-intl";

import { useParams, useRouter } from "next/navigation";
import { useCallback, useOptimistic, useState, useTransition } from "react";
import Skeleton from "../Skeleton";
import styles from "./variant-selector.module.css";

const VariantSelector = ({
  product,
  isLoading,
}: {
  product: PDPProduct;
  isLoading: boolean;
}) => {
  const locale = useLocale();
  const { product: slug } = useParams<{ product: string }>();
  const [open, setOpen] = useState(true);
  const [optimisticSlug, setOptimisticSlug] = useOptimistic(slug);
  const [pending, startTransition] = useTransition();
  const router = useRouter();

  const selectVariant = useCallback(
    (uri: string) => {
      startTransition(() => {
        setOptimisticSlug(uri);

        router.push(`/${locale}/product/${uri}`);
      });
    },
    [locale, router, setOptimisticSlug],
  );

  const prefetchVariant = useCallback(
    (uri: string) => {
      router.prefetch(`/${locale}/product/${uri}`);
    },
    [locale, router],
  );

  return (
    <div className={styles["product-variants-container"]}>
      <div className={styles["title"]}>
        <p>{product?.name}</p>
      </div>
      <div className={clsx(styles["product-variants"], open && styles["open"])}>
        {false ? (
          <VariantSkeleton />
        ) : (
          product?.variants?.map((variant, i) => {
            const isSelected = variant?.uri === optimisticSlug;
            return (
              <button
                className={styles.variantButton}
                role="link"
                key={`${variant.uri}-swatch-${i}`}
                onClick={() => selectVariant(variant?.uri || "#")}
                onMouseOver={() =>
                  variant?.uri && prefetchVariant(variant?.uri)
                }
                title={variant?.name}
              >
                <div
                  className={clsx(
                    styles["product-variant"],
                    isSelected && styles.selected,
                  )}
                >
                  <div
                    className={clsx(
                      styles.swatch,
                      variant?.swatch?.border === "1" && styles.border,
                    )}
                    style={{
                      backgroundColor: `${variant?.swatch?.code}`,
                      backgroundImage: `url(${variant?.swatch?.image?.url})`,
                    }}
                  ></div>
                </div>
              </button>
            );
          })
        )}
      </div>
    </div>
  );
};

export default VariantSelector;

const VariantSkeleton = () => <Skeleton height="var(--skeleton-height)" />;
